import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, Clock, Edit2, Save, Loader } from "lucide-react";
import { apiClient } from "../../services/api";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
} from "@craft-design-system/core-components-react";

// Inject spin keyframe once so the Loader animation works
if (typeof document !== "undefined" && !document.getElementById("vff-sidepanel-spin")) {
	const style = document.createElement("style");
	style.id = "vff-sidepanel-spin";
	style.textContent = `@keyframes vff-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
	document.head.appendChild(style);
}

// Reusable editable part-number field row — defined outside component to keep it stable
const EditablePartField = ({
	field,
	label,
	savingField,
	editingField,
	focusedField,
	tempValues,
	localRow,
	setTempValues,
	setFocusedField,
	handleSaveField,
	handleCancelEdit,
	handleEditField,
	styles,
}) => {
	const isSaving = savingField === field;
	const isEditing = editingField === field;

	return (
		<div style={styles.infoGroup}>
			<div style={styles.infoLabel}>{label}</div>
			<div style={styles.fieldContainer}>
				<div style={styles.fieldInputWrapper}>
					<input
						type="text"
						style={{
							...styles.editableInput,
							...(focusedField === field ? styles.editableInputFocused : {}),
							...(!isEditing ? styles.disabledInput : {}),
						}}
						value={isEditing ? tempValues[field] : localRow?.[field] || ""}
						onChange={(e) =>
							setTempValues((prev) => ({ ...prev, [field]: e.target.value }))
						}
						onFocus={() => setFocusedField(field)}
						onBlur={() => setFocusedField(null)}
						disabled={!isEditing}
						placeholder={`Enter ${label.toLowerCase()}`}
					/>
				</div>
				{isEditing ? (
					<>
						<button
							style={{
								...styles.saveButton,
								...(isSaving ? styles.disabledButton : {}),
							}}
							onClick={() => handleSaveField(field)}
							disabled={isSaving}
							title={isSaving ? "Saving…" : "Save changes"}
						>
							{isSaving ? (
								<Loader
									style={{
										width: "16px",
										height: "16px",
										animation: "vff-spin 0.8s linear infinite",
									}}
								/>
							) : (
								<Save style={{ width: "16px", height: "16px" }} />
							)}
						</button>
						<button
							style={{
								...styles.cancelButton,
								...(isSaving ? styles.disabledButton : {}),
							}}
							onClick={() => handleCancelEdit(field)}
							disabled={isSaving}
							title="Cancel editing"
						>
							<X style={{ width: "16px", height: "16px" }} />
						</button>
					</>
				) : (
					<button
						style={styles.editButton}
						onClick={() => handleEditField(field)}
						title={`Edit ${label}`}
					>
						<Edit2 style={{ width: "16px", height: "16px" }} />
					</button>
				)}
			</div>
		</div>
	);
};

const VFFDataTableSidePanel = ({ isOpen, onClose, selectedRow, onUpdate }) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [worklogModalOpen, setWorklogModalOpen] = useState(false);
	const [fieldTrackingData, setFieldTrackingData] = useState([]);
	const [loadingFieldTracking, setLoadingFieldTracking] = useState(false);
	const [localRow, setLocalRow] = useState(selectedRow);
	const [focusedField, setFocusedField] = useState(null);
	const [editingField, setEditingField] = useState(null);
	const [tempValues, setTempValues] = useState({});
	const [savingField, setSavingField] = useState(null);
	// Tags state
	const [tagsInput, setTagsInput] = useState("");
	const [savingTags, setSavingTags] = useState(false);

	// Get user email for API calls
	const getUserEmail = () => {
		return localStorage.getItem("username") || "user@example.com";
	};

	// Update local row when selectedRow changes
	useEffect(() => {
		console.log("🔍 VFFDataTableSidePanel - selectedRow changed:", selectedRow);
		console.log("🔍 old_snr:", selectedRow?.old_snr);
		console.log("🔍 new_snr:", selectedRow?.new_snr);
		console.log("🔍 change_no:", selectedRow?.change_no);
		console.log("🔍 alg:", selectedRow?.alg);
		setLocalRow(selectedRow);
		setTempValues({
			old_snr: selectedRow?.old_snr || "",
			new_snr: selectedRow?.new_snr || "",
		});
		setTagsInput("");
	}, [selectedRow]);

	// Parse tags string into array
	const parseTags = (tagsStr) => {
		if (!tagsStr) return [];
		return tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
	};

	// Save tags to backend
	const handleSaveTags = async (newTagsStr) => {
		if (!selectedRow?.kast_key) return;
		setSavingTags(true);
		try {
			await apiClient.put(
				"/api/update-vff-tags",
				{
					kast_key: selectedRow.kast_key,
					tags: newTagsStr || null,
				},
				{
					headers: {
						"X-User-Name": getUserEmail() || "Unknown User",
					},
				},
			);
			setLocalRow((prev) => ({ ...prev, tags: newTagsStr || "" }));
			if (onUpdate) {
				onUpdate(selectedRow.kast_key, "tags", newTagsStr || "");
			}
		} catch (error) {
			console.error("Error saving tags:", error);
			alert("Failed to save tags. Please try again.");
		} finally {
			setSavingTags(false);
		}
	};

	// Add a tag
	const handleAddTag = async () => {
		const newTag = tagsInput.trim();
		if (!newTag) return;
		const existing = parseTags(selectedRow?.tags || "");
		if (existing.includes(newTag)) {
			setTagsInput("");
			return;
		}
		const updated = [...existing, newTag].join(",");
		await handleSaveTags(updated);
		setTagsInput("");
	};

	// Remove a tag
	const handleRemoveTag = async (tagToRemove) => {
		const existing = parseTags(selectedRow?.tags || "");
		const updated = existing.filter((t) => t !== tagToRemove).join(",");
		await handleSaveTags(updated);
	};

	// Function to handle field updates (debounced, kept for compatibility)
	const handleFieldUpdate = async (field, value) => {
		if (!selectedRow?.kast_key) return;

		try {
			setLocalRow((prev) => ({ ...prev, [field]: value }));

			if (handleFieldUpdate.timeout) {
				clearTimeout(handleFieldUpdate.timeout);
			}

			handleFieldUpdate.timeout = setTimeout(async () => {
				try {
					const response = await apiClient.post("/api/update-item", {
						id: selectedRow.kast_key,
						field: field,
						value: value,
					});

					if (response.data.success && onUpdate) {
						onUpdate(selectedRow.kast_key, field, value);
					}
				} catch (error) {
					console.error(`Error updating ${field}:`, error);
					setLocalRow((prev) => ({ ...prev, [field]: selectedRow[field] }));
				}
			}, 800);
		} catch (error) {
			console.error(`Error in handleFieldUpdate:`, error);
		}
	};

	// Start editing a field
	const handleEditField = (field) => {
		setEditingField(field);
		setTempValues((prev) => ({ ...prev, [field]: localRow?.[field] || "" }));
	};

	// Cancel editing
	const handleCancelEdit = (field) => {
		setEditingField(null);
		setTempValues((prev) => ({ ...prev, [field]: localRow?.[field] || "" }));
	};

	// Save field value — shows spinner on the save button while in flight
	const handleSaveField = async (field) => {
		if (!selectedRow?.kast_key) return;

		setSavingField(field);
		try {
			const response = await apiClient.post(
				"/api/update-item",
				{
					id: selectedRow.kast_key,
					field: field,
					value: tempValues[field],
				},
				{
					headers: {
						"X-User-Name": getUserEmail() || "Unknown User",
					},
				},
			);

			if (response.data.success) {
				setLocalRow((prev) => ({ ...prev, [field]: tempValues[field] }));
				if (onUpdate) {
					onUpdate(selectedRow.kast_key, field, tempValues[field]);
				}
				setEditingField(null);
			}
		} catch (error) {
			console.error(`Error updating ${field}:`, error);
			alert(
				`Failed to update ${field === "old_snr" ? "Old Part No" : "New Part No"}. Please try again.`,
			);
		} finally {
			setSavingField(null);
		}
	};

	// Fetch field tracking / worklog data
	const fetchFieldTracking = async (kastKey) => {
		if (!kastKey) return;

		setLoadingFieldTracking(true);
		try {
			console.log(`Fetching field tracking for kast_key: ${kastKey}`);
			const encodedKastKey = encodeURIComponent(kastKey);
			const response = await apiClient.get(
				`/api/field-tracking/${encodedKastKey}`,
			);
			console.log(`Response status: ${response.status}`);
			console.log("Field tracking data received:", response.data);
			setFieldTrackingData(response.data.tracking_records || []);
		} catch (error) {
			console.error("Error fetching field tracking data:", error);
			setFieldTrackingData([]);
		} finally {
			setLoadingFieldTracking(false);
		}
	};

	const handleToggleWorklogs = () => {
		if (selectedRow?.kast_key) {
			fetchFieldTracking(selectedRow.kast_key);
		}
		setWorklogModalOpen(true);
	};

	// Close panel when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isOpen && !event.target.closest(".side-panel-container")) {
				onClose();
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen, onClose]);

	// Close panel on Escape
	useEffect(() => {
		const handleEscapeKey = (event) => {
			if (event.key === "Escape" && isOpen) {
				onClose();
			}
		};
		document.addEventListener("keydown", handleEscapeKey);
		return () => document.removeEventListener("keydown", handleEscapeKey);
	}, [isOpen, onClose]);

	// Reset worklog state when switching rows
	useEffect(() => {
		if (selectedRow?.kast_key) {
			setFieldTrackingData([]);
			setWorklogModalOpen(false);
			setLoadingFieldTracking(false);
		}
	}, [selectedRow?.kast_key]);

	if (!isOpen || !selectedRow) return null;

	const toggleCollapse = (e) => {
		e.stopPropagation();
		setIsCollapsed(!isCollapsed);
	};

	const handleClose = (e) => {
		e.stopPropagation();
		onClose();
	};

	const handleCollapsedClick = () => {
		if (isCollapsed) {
			setIsCollapsed(false);
		}
	};

	const styles = {
		container: {
			position: "absolute",
			top: 0,
			right: 0,
			width: isCollapsed ? "50px" : "400px",
			height: "calc(100% - 37px)",
			backgroundColor: isCollapsed && isHovered ? "#e8e8e8" : "#f0f0f0",
			border: "2px solid #1e2832",
			borderRadius: "0",
			borderRight: "none",
			borderTop: "none",
			borderBottom: "none",
			boxShadow: "-4px 0 12px rgba(0, 0, 0, 0.15)",
			transition: "all 0.3s ease",
			zIndex: 1000,
			overflow: "hidden",
			fontFamily: "MAN EUROPE_Regular",
			cursor: isCollapsed ? "pointer" : "default",
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "12px",
			backgroundColor: "#1e2832",
			color: "white",
			justifyContent: isCollapsed ? "center" : "space-between",
			borderBottom: "1px solid #ddd",
			minHeight: "48px",
		},
		title: {
			fontSize: "17px",
			fontFamily: "MAN EUROPE_Bold",
			margin: 0,
			display: isCollapsed ? "none" : "block",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
			color: "white",
		},
		closeButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
			color: "white",
			marginLeft: "8px",
		},
		content: {
			opacity: isCollapsed ? 0 : 1,
			visibility: isCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			padding: isCollapsed ? 0 : "16px",
			flex: 1,
			overflowY: "auto",
			overflowX: "hidden",
		},
		infoGroup: {
			marginBottom: "16px",
			borderBottom: "1px solid #e0e0e0",
			paddingBottom: "12px",
		},
		infoLabel: {
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold",
			color: "#666",
			marginBottom: "4px",
			textTransform: "uppercase",
			letterSpacing: "0.5px",
		},
		infoValue: {
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular",
			color: "#333",
			wordBreak: "break-word",
			lineHeight: "1.4",
		},
		editableInput: {
			width: "100%",
			padding: "8px",
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular",
			color: "#333",
			border: "1px solid #ccc",
			borderRadius: "4px",
			backgroundColor: "#fff",
			transition: "border-color 0.2s ease",
			outline: "none",
		},
		editableInputFocused: {
			borderColor: "#1e2832",
			boxShadow: "0 0 0 2px rgba(30, 40, 50, 0.1)",
		},
		fieldContainer: {
			display: "flex",
			alignItems: "center",
			gap: "8px",
		},
		fieldInputWrapper: {
			flex: 1,
			position: "relative",
			maxWidth: "150px",
		},
		editButton: {
			background: "transparent",
			color: "#666",
			border: "none",
			borderRadius: "4px",
			padding: "6px",
			marginLeft: "10px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			transition: "all 0.2s ease",
			minWidth: "auto",
		},
		saveButton: {
			background: "#cc0000",
			color: "white",
			border: "none",
			borderRadius: "4px",
			padding: "6px",
			marginLeft: "15px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			transition: "background-color 0.2s ease",
			minWidth: "auto",
		},
		cancelButton: {
			background: "#1e2832",
			color: "white",
			border: "none",
			borderRadius: "4px",
			padding: "6px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			transition: "background-color 0.2s ease",
			minWidth: "auto",
		},
		disabledInput: {
			backgroundColor: "#f5f5f5",
			cursor: "not-allowed",
			color: "#666",
		},
		disabledButton: {
			opacity: 0.6,
			cursor: "not-allowed",
		},
		worklogTable: {
			width: "100%",
			borderCollapse: "collapse",
			marginTop: "8px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular",
		},
		worklogHeader: {
			backgroundColor: "#1e2832",
			color: "white",
		},
		worklogHeaderCell: {
			padding: "8px 4px",
			textAlign: "left",
			fontFamily: "MAN EUROPE_Bold",
			fontSize: "11px",
			textTransform: "uppercase",
			letterSpacing: "0.5px",
		},
		worklogRow: {
			borderBottom: "1px solid #e0e0e0",
		},
		worklogCell: {
			padding: "6px 4px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular",
			color: "#333",
		},
		worklogButton: {
			background: "#1e2832",
			color: "white",
			border: "none",
			borderRadius: "4px",
			padding: "8px 12px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "6px",
			transition: "background-color 0.2s ease",
			marginTop: "8px",
		},
		worklogSection: {
			marginTop: "12px",
			borderTop: "1px solid #e0e0e0",
			paddingTop: "12px",
		},
		fieldName: {
			fontSize: "10px",
			fontFamily: "MAN EUROPE_Bold",
			color: "#666",
			textTransform: "capitalize",
		},
	};

	// Shared props for the editable part-number fields
	const editableFieldProps = {
		savingField,
		editingField,
		focusedField,
		tempValues,
		localRow,
		setTempValues,
		setFocusedField,
		handleSaveField,
		handleCancelEdit,
		handleEditField,
		styles,
	};

	return (
		<div
			className="side-panel-container"
			data-tour-target="vff-sidepanel"
			style={styles.container}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={handleCollapsedClick}
		>
			<div style={styles.header}>
				{!isCollapsed && <h3 style={styles.title}>More Info Panel</h3>}
				<div style={{ display: "flex", alignItems: "center" }}>
					<button
						style={styles.toggleButton}
						onClick={toggleCollapse}
						title={isCollapsed ? "Expand panel" : "Collapse panel"}
						aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
					>
						{isCollapsed ? (
							<ChevronLeft style={{ width: "20px", height: "20px" }} />
						) : (
							<ChevronRight style={{ width: "20px", height: "20px" }} />
						)}
					</button>
					{!isCollapsed && (
						<button
							style={styles.closeButton}
							onClick={handleClose}
							title="Close panel"
							aria-label="Close panel"
							data-tour-target="vff-sidepanel-close"
						>
							<X style={{ width: "16px", height: "16px" }} />
						</button>
					)}
				</div>
			</div>

			<div
				style={styles.content}
				onClick={(e) => e.stopPropagation()}
			>
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Vehicle Number</div>
					<div style={styles.infoValue}>{selectedRow?.vehicle_no || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Project Number</div>
					<div style={styles.infoValue}>{selectedRow?.projektnummer || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Part Number</div>
					<div style={styles.infoValue}>{selectedRow?.part_no || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Part Description</div>
					<div style={styles.infoValue}>{selectedRow?.part_description || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>SET</div>
					<div style={styles.infoValue}>{selectedRow?.se_team || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>MB Key</div>
					<div style={styles.infoValue}>{selectedRow?.mb_key || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Programm</div>
					<div style={styles.infoValue}>{selectedRow?.programm || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Kast Key</div>
					<div style={styles.infoValue}>{selectedRow?.kast_key || "N/A"}</div>
				</div>

				{/* Editable Old Part No */}
				<EditablePartField field="old_snr" label="Old Part No" {...editableFieldProps} />

				{/* Editable New Part No */}
				<EditablePartField field="new_snr" label="New Part No" {...editableFieldProps} />

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Baukasten no</div>
					<div style={styles.infoValue}>{selectedRow?.baukasten_no || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Supplier</div>
					<div style={styles.infoValue}>{selectedRow?.supplier || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>VKGR</div>
					<div style={styles.infoValue}>
						{selectedRow?.sales_group && selectedRow.sales_group !== "-"
							? selectedRow.sales_group
							: "N/A"}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>ALG</div>
					<div style={styles.infoValue}>
						{selectedRow?.alg && selectedRow.alg !== "-" ? selectedRow.alg : "N/A"}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Change No</div>
					<div style={styles.infoValue}>{selectedRow?.change_no || "N/A"}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>VKGR ART</div>
					<div style={styles.infoValue}>
						{selectedRow?.vkgr_art && selectedRow.vkgr_art !== "-"
							? selectedRow.vkgr_art
							: "N/A"}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>VKGR ART Description</div>
					<div style={styles.infoValue}>
						{selectedRow?.benenn && selectedRow.benenn !== "-"
							? selectedRow.benenn
							: "N/A"}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>VKGR BEND</div>
					<div style={styles.infoValue}>
						{selectedRow?.vkgr_bend && selectedRow.vkgr_bend !== "-"
							? selectedRow.vkgr_bend
							: "N/A"}
					</div>
				</div>

				{/* Tags Section */}
				<div style={{ ...styles.infoGroup, marginTop: "12px" }}>
					<div style={styles.infoLabel}>Tags</div>
					{/* Existing tags as chips */}
					<div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
						{parseTags(selectedRow?.tags || "").map((tag) => (
							<span
								key={tag}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: "4px",
									backgroundColor: "#1e2832",
									color: "white",
									borderRadius: "12px",
									padding: "2px 8px",
									fontSize: "11px",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								{tag}
								<button
									onClick={() => handleRemoveTag(tag)}
									disabled={savingTags}
									style={{
										background: "none",
										border: "none",
										color: "white",
										cursor: savingTags ? "not-allowed" : "pointer",
										padding: "0",
										lineHeight: 1,
										fontSize: "13px",
										opacity: savingTags ? 0.5 : 1,
									}}
									title={`Remove tag "${tag}"`}
								>
									×
								</button>
							</span>
						))}
						{parseTags(selectedRow?.tags || "").length === 0 && (
							<span style={{ fontSize: "12px", color: "#999", fontFamily: "MAN EUROPE_Regular" }}>
								No tags yet
							</span>
						)}
					</div>
					{/* Add new tag input */}
					<div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
						<input
							type="text"
							value={tagsInput}
							onChange={(e) => setTagsInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleAddTag();
								}
							}}
							placeholder="Add a tag…"
							disabled={savingTags}
							style={{
								flex: 1,
								padding: "4px 8px",
								border: "1px solid #ccc",
								borderRadius: "4px",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Regular",
								outline: "none",
							}}
						/>
						<button
							onClick={handleAddTag}
							disabled={savingTags || !tagsInput.trim()}
							style={{
								background: "#1e2832",
								color: "white",
								border: "none",
								borderRadius: "4px",
								padding: "4px 10px",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								cursor: savingTags || !tagsInput.trim() ? "not-allowed" : "pointer",
								opacity: savingTags || !tagsInput.trim() ? 0.6 : 1,
							}}
						>
							{savingTags ? "…" : "Add"}
						</button>
					</div>
				</div>

				{/* Field Tracking Worklogs */}
				<div style={styles.worklogSection}>
					<button
						style={styles.worklogButton}
						onClick={handleToggleWorklogs}
						title="Show Field Tracking Worklogs"
					>
						<Clock style={{ width: "14px", height: "14px" }} />
						Show Worklogs
					</button>
				</div>
			</div>

			{/* Worklogs Modal */}
			<CraftModal
				show={worklogModalOpen}
				allowClickOut={true}
				onCraftClose={() => setWorklogModalOpen(false)}
			>
				<CraftModalHeader
					header="Field Tracking Worklogs"
					content={`Worklog history for: ${selectedRow?.kast_key || ""}`}
				/>
				<CraftModalBody>
					<div style={{ width: "600px", height: "400px", overflow: "auto" }}>
						{loadingFieldTracking ? (
							<div style={{ textAlign: "center", padding: "20px" }}>
								Loading...
							</div>
						) : fieldTrackingData.length > 0 ? (
							<table style={styles.worklogTable}>
								<thead style={styles.worklogHeader}>
									<tr>
										<th style={styles.worklogHeaderCell}>Date</th>
										<th style={styles.worklogHeaderCell}>Username</th>
										<th style={styles.worklogHeaderCell}>Field</th>
										<th style={styles.worklogHeaderCell}>Value</th>
									</tr>
								</thead>
								<tbody>
									{fieldTrackingData.map((record, index) => (
										<tr key={index} style={styles.worklogRow}>
											<td style={styles.worklogCell}>
												{record.time_track
													? new Date(record.time_track).toLocaleDateString(
															"en-GB",
															{
																year: "numeric",
																month: "2-digit",
																day: "2-digit",
																hour: "2-digit",
																minute: "2-digit",
															},
														)
													: "N/A"}
											</td>
											<td style={styles.worklogCell}>
												{record.username_track || "N/A"}
											</td>
											<td style={{ ...styles.worklogCell, ...styles.fieldName }}>
												{record.track_field || "N/A"}
											</td>
											<td style={styles.worklogCell}>
												{record.track_value || "N/A"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						) : (
							<div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
								No field tracking records found
							</div>
						)}
					</div>
				</CraftModalBody>
				<CraftModalFooter>
					<CraftButton
						variant="primary"
						size="m"
						type="button"
						onClick={() => setWorklogModalOpen(false)}
					>
						Close
					</CraftButton>
				</CraftModalFooter>
			</CraftModal>
		</div>
	);
};

export default VFFDataTableSidePanel;
